import React, { useState } from 'react';
import {
  Box,
  Table,
  TableHeader,
  TableHeaderRow,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Button,
  Text,
  Heading,
  RefreshIcon,
  MailIcon,
  TableToolbar,
} from '@razorpay/blade/components';
import { usePagination } from 'hooks/usePagination';
import { useMissingInformationReport } from './hooks/useMissingInformationReport';
import { getTableToolbarText } from './utils';
import { getStatusBadge } from './viewHelpers';
import { useSendReminder } from './hooks/useSendReminder';
import { SendReminderModal } from './SendReminderModal';
import { ErrorView } from 'components/ui/ErrorView/ErrorView';
import { MissingBankAccount } from 'api/types/missingInformationReportTypes';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { ExportReportButton } from './components/ExportReportButton';
import { BankAccountDetailsDrawer } from './components/BankAccountDetailsDrawer';

export const IncorrectBankAccount = () => {
  const [selectedAccount, setSelectedAccount] = useState<MissingBankAccount | null>(null);
  const [selectedTableRowIds, setSelectedTableRowIds] = useState<(string | number)[]>([]);
  const {
    isSendReminderModalOpen,
    remindEmployeeIds,
    onOpenModal,
    onCloseModal,
    sendReminder,
    isSendingReminder,
  } = useSendReminder('incorrect_bank_account_details');
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const {
    data: employeeData,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useMissingInformationReport('incorrect_bank_account_details', currentPage, itemsPerPage);

  const shouldShowEmptyView =
    !isError && !isLoading && !isFetching && employeeData?.data.items.length === 0;
  const shouldShowErrorView = isError;
  const shouldShowTable = !shouldShowEmptyView && !shouldShowErrorView;

  return (
    <Box>
      <Heading
        size="large"
        weight="semibold"
        color="surface.text.gray.normal"
        marginBottom="spacing.2">
        Incorrect Bank Account Details
      </Heading>
      <Text color="surface.text.gray.subtle" marginBottom="spacing.7">
        Remind employees whose bank account details failed penny-drop validation
      </Text>

      {isError && <ErrorView onRefetch={refetch} />}
      {shouldShowEmptyView && (
        <EmptyListView
          title="🔍 No Missing Information Found"
          description="Great! All employees have completed their details."
        />
      )}
      {shouldShowTable && (
        <Table
          isLoading={isLoading}
          isRefreshing={isFetching}
          data={{
            nodes: employeeData?.data.items || [],
          }}
          onSelectionChange={({ selectedIds }) => {
            setSelectedTableRowIds(selectedIds);
          }}
          selectionType="multiple"
          multiSelectTrigger="checkbox"
          backgroundColor="surface.background.gray.intense"
          toolbar={
            <TableToolbar
              selectedTitle={`${selectedTableRowIds.length} Employees Selected`}
              title={getTableToolbarText(
                employeeData?.data.total_count || 0,
                currentPage,
                itemsPerPage,
              )}>
              <Box display="flex" justifyContent="flex-end" gap="spacing.5">
                <ExportReportButton type="incorrect_bank_account_details" />
                <Button
                  icon={MailIcon}
                  isDisabled={selectedTableRowIds.length === 0}
                  onClick={() => {
                    onOpenModal({
                      remindEmployeeIds: selectedTableRowIds,
                    });
                  }}>
                  Send Reminder
                </Button>
                <Button
                  icon={RefreshIcon}
                  variant="tertiary"
                  accessibilityLabel="Refresh Incorrect Bank Account Details"
                  onClick={refetch}
                  isDisabled={isFetching || isLoading}
                />
              </Box>
            </TableToolbar>
          }
          pagination={
            <TablePagination
              paginationType="server"
              showPageSizePicker={true}
              currentPage={currentPage}
              totalItemCount={employeeData?.data.total_count || 0}
              onPageChange={({ page }) => setCurrentPage(page)}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
                setCurrentPage(0);
              }}
            />
          }>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Account Number</TableHeaderCell>
                  <TableHeaderCell>IFSC Code</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((employee) => (
                  <TableRow
                    key={employee.id}
                    item={employee}
                    hoverActions={
                      <Box display="flex" gap="spacing.4">
                        <Button
                          size="xsmall"
                          variant="tertiary"
                          icon={MailIcon}
                          iconPosition="left"
                          onClick={() => {
                            onOpenModal({
                              remindEmployeeIds: [employee.id],
                            });
                          }}>
                          Send Reminder
                        </Button>
                        <Button
                          size="xsmall"
                          variant="secondary"
                          onClick={() => setSelectedAccount(employee)}>
                          View Details
                        </Button>
                      </Box>
                    }>
                    <TableCell>
                      <Box>
                        <Text color="surface.text.gray.subtle">{employee.name}</Text>
                        <Text size="small" color="surface.text.gray.muted">
                          {employee.employee_id}
                        </Text>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text color="surface.text.gray.subtle">{employee.account_number || '-'}</Text>
                    </TableCell>
                    <TableCell>
                      <Text color="surface.text.gray.subtle">{employee.ifsc || '-'}</Text>
                    </TableCell>
                    <TableCell>{getStatusBadge(employee.verification_status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      )}
      <SendReminderModal
        isOpen={isSendReminderModalOpen}
        onClose={onCloseModal}
        description={
          remindEmployeeIds.length === 1
            ? 'RazorpayX Payroll will email this employee to remind them to update their bank account details.'
            : 'RazorpayX Payroll will email each selected person to update their incorrect bank account details.'
        }
        onSendReminder={() => {
          sendReminder({
            reminder_type: 'incorrect_bank_account_details',
            employee_ids: remindEmployeeIds.map((id) => Number(id)),
          });
        }}
        isSendingReminder={isSendingReminder}
      />
      <BankAccountDetailsDrawer
        selectedAccount={selectedAccount}
        onClose={() => setSelectedAccount(null)}
        variant="incorrect"
      />
    </Box>
  );
};
